import React from 'react';
import { Box, Typography, Checkbox, FormControlLabel } from '@mui/material';
import {
  sectionStyles,
  typographyStyles,
  checkboxStyles,
} from '../../../modals/unified_config_modals/styles/unifiedConfigStyles';
import { useTranslation } from 'react-i18next';
import { StringKeys } from '@/resources/strings/StringKeys';
import type { PvdConfigFieldsReturn } from '@/hooks/pvd_config_hooks/usePvdConfigFields';
import Flat22SensitivityField from './Flat22SensitivityField';
import SmartAdaptiveExpansion from './SmartAdaptiveExpansion';

const VOLTAGE_CHANNEL_MIN = 1;
const VOLTAGE_CHANNEL_MAX = 22;

interface Props {
  editor: PvdConfigFieldsReturn;
  voltageEnabled: boolean;
  setVoltageEnabled: (v: boolean) => void;
  readonly?: boolean;
  onChipHover?: (sensitivity: number) => void;
  onChipLeave?: () => void;
}

export const VoltageSensitivitySection: React.FC<Props> = ({
  editor,
  voltageEnabled,
  setVoltageEnabled,
  readonly = false,
  onChipHover,
  onChipLeave,
}) => {
  const { t } = useTranslation();

  const handleHeaderToggle = (next: boolean) => {
    setVoltageEnabled(next);
    if (next) {
      editor.enableAllVoltage();
      editor.setAdaptiveVoltageAllowed(true);
    } else {
      editor.disableAllVoltage();
      editor.setAdaptiveVoltageAllowed(false);
    }
  };

  return (
    <Box
      sx={{
        ...sectionStyles.voltageSection,
        minHeight: { md: readonly ? 'auto' : 624 },
      }}
    >
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.25 }}>
          <Typography
            variant="h6"
            sx={{ ...typographyStyles.sectionHeading, mr: 2 }}
          >
            {t(StringKeys.VOLTAGE_SENSITIVITY)}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={voltageEnabled}
                onChange={(e) => handleHeaderToggle(e.target.checked)}
                sx={checkboxStyles.sectionToggle(voltageEnabled)}
              />
            }
            label={
              <Typography sx={typographyStyles.checkboxLabel(voltageEnabled)}>
                {voltageEnabled
                  ? t(StringKeys.ENABLED)
                  : t(StringKeys.DISABLED)}
              </Typography>
            }
          />
        </Box>
        <Typography sx={{ ...typographyStyles.description, mb: 0 }}>
          {t(StringKeys.VOLTAGE_SENSITIVITY_SECTION_DESCRIPTION)}
        </Typography>
      </Box>

      <Flat22SensitivityField
        values={editor.voltage}
        onToggle={(i) => editor.toggleVoltageChip(i)}
        smartAdaptive={editor.adaptiveVoltageAllowed}
        setSmartAdaptive={editor.setAdaptiveVoltageAllowed}
        onEnableAll={() => editor.enableAllVoltage()}
        onDisableAll={() => {
          editor.disableAllVoltage();
          editor.setAdaptiveVoltageAllowed(false);
        }}
        readonly={readonly}
        onChipHover={onChipHover}
        onChipLeave={onChipLeave}
      />

      {editor.adaptiveVoltageAllowed && !readonly && (
        <SmartAdaptiveExpansion
          channel="voltage"
          bubbleSizeIsLocked={editor.voltageBubbleSizeIsLocked}
          bubbleSizeLockedToValue={editor.voltageBubbleSizeLockedToValue}
          setBubbleUserDefined={editor.setVoltageBubbleUserDefined}
          setBubbleSmall={editor.setVoltageBubbleSmall}
          setBubbleLarge={editor.setVoltageBubbleLarge}
          autoAdaptIsLocked={editor.autoAdaptIsLocked}
          autoAdaptLockedToValue={editor.autoAdaptLockedToValue}
          setAutoAdaptUserDefined={editor.setAutoAdaptUserDefined}
          setAutoAdaptOn={editor.setAutoAdaptOn}
          setAutoAdaptOff={editor.setAutoAdaptOff}
          channelMin={VOLTAGE_CHANNEL_MIN}
          channelMax={VOLTAGE_CHANNEL_MAX}
          minValue={editor.voltageMinSetting}
          maxValue={editor.voltageMaxSetting}
          onRangeChange={editor.setVoltageRange}
        />
      )}
    </Box>
  );
};
